<template>
  <div
    v-if="isImage && imageUrl"
    class="tw-h-full tw-w-full tw-overflow-hidden"
  >
    <img
      :src="imageUrl"
      :alt="attachment.name"
      class="tw-h-full tw-w-full tw-object-cover"
    />
  </div>

  <div
    v-else
    class="tw-text-4xs tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-text-center"
  >
    <q-icon :name="`fa-duotone ${attachmentComponent}`" size="22px" />

    <div
      class="tw-line-clamp-2 tw-overflow-ellipsis tw-text-balance tw-break-all tw-text-2xs"
    >
      {{ attachment.name }}
    </div>
  </div>
</template>

<script setup>
import { iconByFileExtension } from "common/fileHelpers";
import { computed, onBeforeUnmount } from "vue";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "heif", "heic"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "mkv", "webm", "avi", "wmv"];

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const extension = computed(() =>
  props.attachment.name.split(".").pop().toLowerCase(),
);

const isVideo = computed(() => {
  const mimetype = props.attachment.mimetype || props.attachment.type || "";

  if (mimetype.startsWith("video/")) {
    return true;
  }

  return VIDEO_EXTENSIONS.includes(extension.value);
});

const attachmentComponent = computed(() => {
  if (isVideo.value) {
    return "fa-file-video";
  }

  return iconByFileExtension(extension.value);
});

const isImage = computed(() => {
  // Check mimetype first (available on both Attachment and File objects)
  const mimetype = props.attachment.mimetype || props.attachment.type || "";

  if (mimetype.startsWith("image/")) {
    return true;
  }

  return IMAGE_EXTENSIONS.includes(extension.value);
});

const imageUrl = computed(() => {
  // Existing Attachment object — use previewUrl or url
  if (props.attachment.previewUrl) {
    return props.attachment.previewUrl;
  }

  if (props.attachment.url) {
    return props.attachment.url;
  }

  // New File object — create object URL
  if (props.attachment instanceof File) {
    return URL.createObjectURL(props.attachment);
  }

  return null;
});

const thumbnailUrl = computed(() => props.attachment.thumbnail_url || null);

onBeforeUnmount(() => {
  // Revoke object URLs to prevent memory leaks
  if (props.attachment instanceof File && imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
  }
});
</script>
